import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Flex,
	Space,
	Table,
	Tag,
	Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from 'react-query';
import { uniqueOptions } from '@shared/utils/array';
import {
	CargoUnitLabels,
	LinerCostUnitType,
	PortActionTypes,
	VesselTypeLabels,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { formatNumber } from '@shared/utils/formatNumber';
import type { Port } from '@api/utils/ports';
import type { HistoricalDARow } from '@api/features/disbursement-accounts/getHistoricalDisbursementAccounts';
import type { CargoDetails } from '@api/features/cargos/getCargoDetails';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import { getHistoricalDisbursementAccounts } from '@client/lib/api';
import NumericInput from '@client/components/NumericInput';
import Select from '@client/components/Select';
import CountryFlag from '@client/components/CountryFlag';
import { formatDate } from '@client/utils/formatDate';
import Button from '@client/components/Button';
import Modal from '@client/components/Modal/Modal';
import {
	LocalPortRotation,
	PartialPortRotationEntry,
} from './context/hooks/usePortRotationHandlers';
import { useEstimateChangesProvider } from './context/EstimateChangesContext';
import styles from './styles/shared.module.css';

type Props = {
	open: boolean;
	onClose: () => void;
	record: LocalPortRotation;
	port: Port;
	disabled?: boolean;
	cargos: CargoDetails[];
	onPortRotationRowChange: (
		id: number,
		attributes: any,
	) => void;
};

const HistoricalPortCostModal = ({
	open,
	onClose,
	record,
	port,
	disabled,
	cargos,
	onPortRotationRowChange,
}: Props) => {
	const { immediateSaveChanges, saving } = useEstimateChangesProvider();
	const [localPortCost, setLocalPortCost] = useState<number | null>(null);
	const [localLinerCost, setLocalLinerCost] = useState<number | null>(null);
	const [localLinerCostUnit, setLocalLinerCostUnit] = useState<LinerCostUnitType | null>(null);
	const [dirty, setDirty] = useState(false);
	const [page, setPage] = useState(1);

	useEffect(() => {
		if (open && record) {
			setLocalPortCost(record.portCost ?? null);
			setLocalLinerCost(record.linerCostPricePerUnit ?? null);
			setLocalLinerCostUnit(record.linerCostUnit ?? null);
			setDirty(false);
			setPage(1);
		}
	}, [open, record?.id]);

	const { data, isLoading } = useQuery(
		['historicalDAs', port?.id, page],
		() => getHistoricalDisbursementAccounts(port.id, page),
		{
			enabled: open && port?.id != null,
			keepPreviousData: true,
		},
	);

	const handleSave = useCallback(() => {
		if (record?.id == null) {
			return;
		}

		const attrs: Partial<PartialPortRotationEntry> = {};

		if (localPortCost !== record.portCost) {
			attrs.portCost = localPortCost;
		}

		if (localLinerCost !== record.linerCostPricePerUnit) {
			attrs.linerCostPricePerUnit = localLinerCost;
		}

		if (localLinerCostUnit !== record.linerCostUnit) {
			attrs.linerCostUnit = localLinerCostUnit;
		}

		if (Object.keys(attrs).length > 0) {
			immediateSaveChanges(() => {
				onPortRotationRowChange(record.id, attrs);
			});
		}

		setDirty(false);
	}, [record, localPortCost, localLinerCost, localLinerCostUnit, onPortRotationRowChange, immediateSaveChanges, onClose]);

	const saveButton = useSaveButton({
		onSave: handleSave,
		loading: isLoading || saving,
		hasPendingChanges: open && dirty && !disabled,
		visible: open,
	});

	const { useBlocker, makeBlockable } = useNavigationBlock();
	const handleClose = makeBlockable(() => {
		setDirty(false);
		onClose();
	});
	useBlocker(open && dirty, 'You have unsaved changes. Are you sure you want to close?');

	const relevantCargo = cargos.find((c) => c.id === record?.cargoId);

	const showLinerFields = (
		record?.type === PortActionTypes.LOADING ||
		record?.type === PortActionTypes.DISCHARGING
	) && relevantCargo != null;

	const linerUnitOptions = useMemo(() => {
		if (!relevantCargo) {
			return [];
		}

		if (relevantCargo.unit === relevantCargo.secondaryUnit) {
			return [{ label: CargoUnitLabels[relevantCargo.unit], value: relevantCargo.unit }];
		}

		return [
			{ label: CargoUnitLabels[relevantCargo.unit], value: relevantCargo.unit },
			{ label: CargoUnitLabels[relevantCargo.secondaryUnit], value: relevantCargo.secondaryUnit },
		];
	}, [relevantCargo]);

	const uniqueVesselTypes = useMemo(() => {
		if (!data?.rows) {
			return [];
		}
	
		return uniqueOptions(
			data.rows.map((r) => r.vesselType),
			(t) => VesselTypeLabels[t as keyof typeof VesselTypeLabels] ?? t,
		);
	}, [data?.rows]);
	
	const uniqueVesselSizes = useMemo(() => {
		if (!data?.rows) {
			return [];
		}
	
		return uniqueOptions(
			data.rows.map((r) => r.vesselSize),
		);
	}, [data?.rows]);
	
	const uniqueActivities = useMemo(() => {
		if (!data?.rows) {
			return [];
		}
	
		return uniqueOptions(
			data.rows.flatMap((r) => r.portActivities ?? []),
		);
	}, [data?.rows]);

	const expandedRowRender = (row: HistoricalDARow) => {
		const costItemCols: ColumnsType<HistoricalDARow['costItems'][number]> = [
			{ title: 'Type', dataIndex: 'type', key: 'type' },
			{ title: 'Category', dataIndex: 'category', key: 'category' },
			{
				title: 'Amount',
				dataIndex: 'amount',
				key: 'amount',
				align: 'right',
				render: (_, item) => formatCurrency(item.amount, row.currency ),
			},
		];

		return (
			<Table
				columns={costItemCols}
				dataSource={row.costItems}
				rowKey={(_, i) => `${row.id}-cost-${i}`}
				pagination={false}
				size="small"
			/>
		);
	};

	const columns: ColumnsType<HistoricalDARow> = [
		{
			title: 'Status',
			dataIndex: 'pdaFdaStatus',
			key: 'pdaFdaStatus',
			width: 80,
			render: (status: string) => <Tag>{status}</Tag>,
		},
		{
			title: 'Vessel',
			dataIndex: 'vesselName',
			key: 'vesselName',
			sorter: (a, b) => a.vesselName.localeCompare(b.vesselName),
			filterSearch: true,
			onFilter: (value, row) => row.vesselName.toLowerCase().includes(String(value).toLowerCase()),
		},
		{
			title: 'Type',
			dataIndex: 'vesselType',
			key: 'vesselType',
			width: 120,
			filters: uniqueVesselTypes,
			onFilter: (value, row) => row.vesselType === value,
			render: (type: string) => VesselTypeLabels[type as keyof typeof VesselTypeLabels] ?? type ?? '-',
		},
		{
			title: 'Size',
			dataIndex: 'vesselSize',
			key: 'vesselSize',
			width: 100,
			filters: uniqueVesselSizes,
			onFilter: (value, row) => row.vesselSize === value,
			render: (size: string) => size ?? '-',
		},
		{
			title: 'DWT',
			dataIndex: 'vesselDwt',
			key: 'vesselDwt',
			width: 100,
			sorter: (a, b) => (a.vesselDwt ?? 0) - (b.vesselDwt ?? 0),
			render: (dwt: number | null) => (dwt != null ? formatNumber(dwt) : '-'),
		},
		{
			title: 'Contract ID',
			dataIndex: 'contractId',
			key: 'contractId',
			width: 120,
		},
		{
			title: 'Departure',
			dataIndex: 'departure',
			key: 'departure',
			width: 130,
			defaultSortOrder: 'descend',
			render: (dep: string | null) => (dep ? formatDate(dep) : '-'),
		},
		{
			title: 'Port Activities',
			dataIndex: 'portActivities',
			key: 'portActivities',
			width: 160,
			filters: uniqueActivities,
			onFilter: (value, row) => row.portActivities.includes(String(value)),
			render: (activities: string[]) => (
				<Space size={[0, 4]} wrap>
					{activities.map((a) => <Tag key={a}>{a}</Tag>)}
				</Space>
			),
		},
		{
			title: 'Cargos',
			dataIndex: 'cargos',
			key: 'cargos',
			width: 140,
			render: (c: string[]) => c.join(', ') || '-',
		},
		{
			title: 'Total cost (ex. liner)',
			dataIndex: 'totalCostExLiner',
			key: 'totalCostExLiner',
			width: 160,
			align: 'right',
			sorter: (a, b) => a.totalCostExLiner - b.totalCostExLiner,
			render: (_: number, row: HistoricalDARow) => formatCurrency(row.totalCostExLiner, row.currency),
		},
		{
			title: 'Total cost (inc. liner)',
			dataIndex: 'totalCostIncLiner',
			key: 'totalCostIncLiner',
			width: 160,
			align: 'right',
			sorter: (a, b) => a.totalCostIncLiner - b.totalCostIncLiner,
			render: (_: number, row: HistoricalDARow) => formatCurrency(row.totalCostIncLiner, row.currency),
		},
	];

	return (
		<Modal
			title="Historical Port Costs"
			open={open}
			onCancel={handleClose}
			width={1400}
			destroyOnClose
			footer={(
				<Flex justify="flex-end" gap={8}>
					<Button
						onClick={handleClose}
					>
						Cancel
					</Button>
					{saveButton}
				</Flex>
			)}
		>
			<Flex align="center" gap={12} style={{ marginBottom: 16 }}>
				<CountryFlag countryCode={port?.countryCode} size={28} />
				<div>
					<Typography.Title level={5} style={{ margin: 0 }}>
						{port?.name}
					</Typography.Title>
					<Typography.Text type="secondary">
						{port?.countryName ?? port?.countryCode}
					</Typography.Text>
				</div>
			</Flex>

			<Flex gap={16} style={{ marginBottom: 20 }}>
				<div style={{ width: 180 }}>
					<Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
						Port DA
					</Typography.Text>
					<NumericInput
						disabled={disabled}
						value={localPortCost ?? 0}
						onChange={(val) => {
							setLocalPortCost(val);
							setDirty(true);
						}}
						data-testid="historical-port-da"
					/>
				</div>
				{showLinerFields && (
					<>
						<div style={{ width: 180 }}>
							<Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
								Liner Cost
							</Typography.Text>
							<NumericInput
								disabled={disabled}
								value={localLinerCost ?? 0}
								onChange={(val) => {
									setLocalLinerCost(val);
									setDirty(true);
								}}
								data-testid="historical-liner-cost"
							/>
						</div>
						<div style={{ width: 140 }}>
							<Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
								Liner Cost Units
							</Typography.Text>
							<Select
								disabled={disabled || linerUnitOptions.length <= 1}
								value={localLinerCostUnit ?? relevantCargo?.unit}
								options={linerUnitOptions}
								onChange={(val) => {
									setLocalLinerCostUnit(val);
									setDirty(true);
								}}
								style={{ width: '100%' }}
								data-testid="historical-liner-unit"
							/>
						</div>
					</>
				)}
			</Flex>
			<Table<HistoricalDARow>
				className={styles.table}
				columns={columns}
				dataSource={data?.rows ?? []}
				rowKey="id"
				loading={isLoading}
				size="small"
				expandable={{
					expandedRowRender,
				}}
				pagination={{
					current: page,
					pageSize: 10,
					total: data?.total ?? 0,
					showSizeChanger: false,
					onChange: (p) => setPage(p),
				}}
				locale={{
					emptyText: 'There are no historical DAs for this port',
				}}
			/>
		</Modal>
	);
};

export default HistoricalPortCostModal;
